import { Button } from 'designSystem/components/Button'
import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useRolodexSignedURLs } from 'features/rolodex'
import { Download } from 'lucide-react'
import type { ComponentProps, MouseEvent } from 'react'
import { useMemo, useState } from 'react'
import { downloadFromUrl } from 'utils/download'

interface DownloadSearchResultButtonProps {
  document: DocumentTableRow
  size?: ComponentProps<typeof Button>['size']
}

export function DownloadSearchResultButton({
  document,
  size = 'icon-lg',
}: DownloadSearchResultButtonProps) {
  const [isDownloading, setIsDownloading] = useState(false)

  const urlParams = useMemo(
    () =>
      document.filename
        ? [{ entry_id: document.id, path: document.filename }]
        : undefined,
    [document.filename, document.id]
  )
  const { url, isLoading: urlLoading } = useRolodexSignedURLs(urlParams)

  const isDisabled = !url || !document.filename || urlLoading

  const handleDownload = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    event.stopPropagation()

    if (!url || !document.filename) return

    setIsDownloading(true)
    try {
      await downloadFromUrl(url, document.filename)
    } catch (error) {
      console.error('Failed to download document:', error)
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Button
      type='button'
      variant='inputRaised'
      size={size}
      title='Download Document'
      aria-label={`Download ${document.documentTitle || document.title}`}
      onClick={handleDownload}
      disabled={isDisabled || isDownloading}>
      <Download size={18} strokeWidth={1.5} />
    </Button>
  )
}
